'use client';

import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { m } from 'motion/react';
import { css, cx } from 'styled-system/css';
import { card } from 'styled-system/recipes';

import { Collapsible } from '@shaojiang/components/ui/collapsible';

interface DisclosureProps {
  title: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
}

/** A card that folds its details away until asked (keeps pages short). */
export function Disclosure({ title, children, defaultOpen = false, className }: DisclosureProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible.Root
      open={open}
      onOpenChange={(details) => setOpen(details.open)}
      className={cx(card().root, css({ overflow: 'visible' }), className)}
    >
      <Collapsible.Trigger
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '4',
          w: 'full',
          px: '6',
          py: '5',
          textStyle: 'h4',
          cursor: 'pointer',
          textAlign: 'start',
          bg: 'transparent',
          border: 'none',
          color: 'fg.default',
          borderRadius: 'card',
        })}
      >
        {title}
        <m.span
          animate={{ rotate: open ? 180 : 0 }}
          className={css({ display: 'inline-flex', color: 'fg.muted' })}
        >
          <ChevronDown />
        </m.span>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <div className={css({ px: '6', pb: '6' })}>{children}</div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

/** Bullet list of what I did on a project. */
export function DoneList({ items }: { items: string[] }) {
  return (
    <ul
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '3',
        m: '0',
        p: '0',
        listStyle: 'none',
        textStyle: 'sm',
        color: 'fg.muted',
      })}
    >
      {items.map((item) => (
        <li key={item} className={css({ display: 'flex', gap: '3' })}>
          <span
            aria-hidden
            className={css({
              flexShrink: '0',
              mt: '0.5em',
              w: '2',
              h: '2',
              borderRadius: 'full',
              bg: 'fg.hot',
              _nebula: { bg: 'fg.accent' },
            })}
          />
          {item}
        </li>
      ))}
    </ul>
  );
}
